/** Sesiones Deportes (Running / Natación) vs Gimnasio — Training módulo. */

export const SESSION_KINDS = Object.freeze({
  GYM: 'gym',
  RUNNING: 'running',
  SWIMMING: 'swimming',
});

export const RUNNING_SURFACES = Object.freeze([
  { id: 'asphalt', label: 'Running (asfalto)' },
  { id: 'trail', label: 'Trail (montaña)' },
  { id: 'track_100', label: 'Pista 100 m' },
  { id: 'track_400', label: 'Pista 400 m' },
  { id: 'track_800', label: 'Pista 800 m' },
]);

export const POOL_LENGTHS = Object.freeze([12, 25, 50]);

export const RUN_TYPES = Object.freeze(['easy', 'long', 'intervals', 'tempo', 'recovery']);

export const RUN_TYPE_LABELS = Object.freeze({
  easy: 'Rodaje fácil',
  long: 'Tirada larga',
  intervals: 'Series / intervalos',
  tempo: 'Tempo / umbral',
  recovery: 'Recuperación activa',
});

export function normalizeSessionKind(raw) {
  const v = String(raw || '').toLowerCase().trim();
  if (v === 'running' || v === 'run') return SESSION_KINDS.RUNNING;
  if (v === 'swimming' || v === 'swim' || v === 'natacion' || v === 'natación') {
    return SESSION_KINDS.SWIMMING;
  }
  return SESSION_KINDS.GYM;
}

export function isSportSessionKind(kind) {
  const k = normalizeSessionKind(kind);
  return k === SESSION_KINDS.RUNNING || k === SESSION_KINDS.SWIMMING;
}

export function inferSessionKindFromExercises(day = {}) {
  const row = day && typeof day === 'object' ? day : {};
  const blob = (row.ejercicios || [])
    .map((e) => String(e.exercise_name || e.nombre || ''))
    .join(' ');
  if (!/sesi[oó]n\s+deportes/i.test(blob)) return null;
  if (/nataci[oó]n/i.test(blob)) return SESSION_KINDS.SWIMMING;
  if (/running|\brun\b/i.test(blob)) return SESSION_KINDS.RUNNING;
  return SESSION_KINDS.RUNNING;
}

export function resolveDaySessionKind(day = {}) {
  const row = day && typeof day === 'object' ? day : {};
  const k = normalizeSessionKind(row.session_kind);
  if (isSportSessionKind(k)) return k;
  return inferSessionKindFromExercises(row) || k;
}

export function emptySportPrescription(kind = SESSION_KINDS.RUNNING) {
  const k = normalizeSessionKind(kind);
  if (k === SESSION_KINDS.SWIMMING) {
    return {
      pool_length_m: 25,
      zone_target: 2,
      hr_target_ppm: null,
      pace_per_100_sec: null,
    };
  }
  if (k === SESSION_KINDS.RUNNING) {
    return {
      surface: 'asphalt',
      run_type: 'easy',
      zone_target: 2,
      hr_target_ppm: null,
      pace_target_sec_per_km: null,
      distance_target_km: null,
      include_brick: false,
      transition_bike_run_pace_sec: null,
      intervals_note: '',
    };
  }
  return null;
}

export function emptySportLog(kind = SESSION_KINDS.RUNNING) {
  const k = normalizeSessionKind(kind);
  const base = {
    duration_h: '',
    duration_m: '',
    duration_s: '',
    hr_avg_ppm: '',
    zone: '',
    calories: '',
    notes: '',
  };
  if (k === SESSION_KINDS.SWIMMING) {
    return {
      ...base,
      pool_length_m: 25,
      distance_m: '',
      pace_per_100_sec: '',
    };
  }
  return {
    ...base,
    surface: 'asphalt',
    distance_km: '',
    pace_min: '',
    pace_sec: '',
    transition_bike_run_pace_sec: '',
  };
}

/** HH/MM/SS digitados → segundos totales. */
export function sportDurationToSec(log = {}) {
  const h = Math.max(0, Number(log.duration_h) || 0);
  const m = Math.max(0, Number(log.duration_m) || 0);
  const s = Math.max(0, Number(log.duration_s) || 0);
  return h * 3600 + m * 60 + s;
}

export function sportDurationFromSec(totalSec) {
  const n = Math.max(0, Math.floor(Number(totalSec) || 0));
  return {
    duration_h: String(Math.floor(n / 3600)),
    duration_m: String(Math.floor((n % 3600) / 60)),
    duration_s: String(n % 60),
  };
}

export function paceSecFromMinSec(paceMin, paceSec) {
  const m = Math.max(0, Number(paceMin) || 0);
  const s = Math.max(0, Number(paceSec) || 0);
  return m * 60 + s;
}

export function pacePartsFromSec(secPerKm) {
  const n = Math.max(0, Math.round(Number(secPerKm) || 0));
  if (n <= 0) return { min: '', sec: '' };
  return { min: String(Math.floor(n / 60)), sec: String(n % 60) };
}

/** Si hay tiempo + km y el pace está vacío, lo calcula. */
export function derivePaceFromTimeAndDistance(log = {}) {
  const sec = sportDurationToSec(log);
  const km = Number(log.distance_km);
  if (!(sec > 0 && km > 0)) return null;
  const pace = Math.round(sec / km);
  const parts = pacePartsFromSec(pace);
  return { pace_min: parts.min, pace_sec: parts.sec };
}

export function formatPaceLabel(secPerKm) {
  const n = Number(secPerKm);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}:${String(s).padStart(2, '0')} /km`;
}

export function formatDurationLabel(totalSec) {
  const n = Math.max(0, Math.floor(Number(totalSec) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

export function sportLogHasAthleteData(log) {
  if (!log || typeof log !== 'object') return false;
  if (sportDurationToSec(log) > 0) return true;
  if (String(log.hr_avg_ppm || '').trim()) return true;
  if (String(log.zone || '').trim()) return true;
  if (String(log.calories || '').trim()) return true;
  if (String(log.distance_km || '').trim() || String(log.distance_m || '').trim()) return true;
  if (String(log.pace_min || '').trim() || String(log.pace_sec || '').trim()) return true;
  if (String(log.pace_per_100_sec || '').trim()) return true;
  if (String(log.notes || '').trim()) return true;
  return false;
}

/** Normaliza sport_log para API (números). */
export function serializeSportLog(kind, log = {}) {
  const k = normalizeSessionKind(kind);
  const duration_sec = sportDurationToSec(log);
  const out = {
    duration_sec,
    hr_avg_ppm: log.hr_avg_ppm !== '' && log.hr_avg_ppm != null ? Number(log.hr_avg_ppm) : null,
    zone: log.zone !== '' && log.zone != null ? Number(log.zone) : null,
    calories: log.calories !== '' && log.calories != null ? Number(log.calories) : null,
    notes: String(log.notes || '').trim() || null,
  };
  if (k === SESSION_KINDS.SWIMMING) {
    out.pool_length_m = Number(log.pool_length_m) || 25;
    out.distance_m = log.distance_m !== '' && log.distance_m != null ? Number(log.distance_m) : null;
    out.pace_per_100_sec = log.pace_per_100_sec !== '' && log.pace_per_100_sec != null
      ? Number(log.pace_per_100_sec)
      : null;
  } else if (k === SESSION_KINDS.RUNNING) {
    out.surface = log.surface || 'asphalt';
    out.distance_m = log.distance_km !== '' && log.distance_km != null
      ? Math.round(Number(log.distance_km) * 1000)
      : null;
    let paceSec = paceSecFromMinSec(log.pace_min, log.pace_sec);
    const km = log.distance_km !== '' && log.distance_km != null ? Number(log.distance_km) : 0;
    if (paceSec <= 0 && duration_sec > 0 && km > 0) {
      paceSec = Math.round(duration_sec / km);
    }
    out.pace_sec_per_km = paceSec > 0 ? paceSec : null;
    out.transition_bike_run_pace_sec = log.transition_bike_run_pace_sec !== ''
      && log.transition_bike_run_pace_sec != null
      ? Number(log.transition_bike_run_pace_sec)
      : null;
  }
  return out;
}

export function hydrateSportLogFromApi(kind, sportLog) {
  const k = normalizeSessionKind(kind);
  const base = emptySportLog(k);
  if (!sportLog || typeof sportLog !== 'object') return base;
  const dur = sportDurationFromSec(sportLog.duration_sec);
  const common = {
    ...base,
    ...dur,
    hr_avg_ppm: sportLog.hr_avg_ppm != null ? String(sportLog.hr_avg_ppm) : '',
    zone: sportLog.zone != null ? String(sportLog.zone) : '',
    calories: sportLog.calories != null ? String(sportLog.calories) : '',
    notes: sportLog.notes || '',
  };
  if (k === SESSION_KINDS.SWIMMING) {
    return {
      ...common,
      pool_length_m: sportLog.pool_length_m || 25,
      distance_m: sportLog.distance_m != null ? String(sportLog.distance_m) : '',
      pace_per_100_sec: sportLog.pace_per_100_sec != null ? String(sportLog.pace_per_100_sec) : '',
    };
  }
  const pace = Number(sportLog.pace_sec_per_km) || 0;
  return {
    ...common,
    surface: sportLog.surface || 'asphalt',
    distance_km: sportLog.distance_m != null
      ? String(Math.round((Number(sportLog.distance_m) / 1000) * 100) / 100)
      : '',
    pace_min: pace > 0 ? String(Math.floor(pace / 60)) : '',
    pace_sec: pace > 0 ? String(Math.round(pace % 60)) : '',
    transition_bike_run_pace_sec: sportLog.transition_bike_run_pace_sec != null
      ? String(sportLog.transition_bike_run_pace_sec)
      : '',
  };
}

export function aggregateSportLogs(logs = [], days = 30) {
  const cutoff = Date.now() - days * 864e5;
  const sport = (logs || []).filter((l) => {
    if (!isSportSessionKind(l?.session_kind)) return false;
    const t = Date.parse(String(l.fecha || l.created_at || ''));
    return Number.isFinite(t) ? t >= cutoff : true;
  });
  const running = sport.filter((l) => normalizeSessionKind(l.session_kind) === SESSION_KINDS.RUNNING);
  const swimming = sport.filter((l) => normalizeSessionKind(l.session_kind) === SESSION_KINDS.SWIMMING);
  const avg = (arr, fn) => {
    const vals = arr.map(fn).filter((n) => Number.isFinite(n) && n > 0);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };
  return {
    total: sport.length,
    running: running.length,
    swimming: swimming.length,
    avg_pace_sec_per_km: avg(running, (l) => Number(l.sport_log?.pace_sec_per_km)),
    avg_zone: avg(sport, (l) => Number(l.sport_log?.zone)),
    avg_hr: avg(sport, (l) => Number(l.sport_log?.hr_avg_ppm)),
    total_duration_sec: sport.reduce((a, l) => a + (Number(l.sport_log?.duration_sec) || 0), 0),
    sessions: sport.slice().sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || ''))),
  };
}
